import { useEffect, useState } from 'react'
import { Box, Button, Card, CardBody, CardHeader, Heading, Spinner, Text } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { ParentTicketForm } from '@/components/forms/ParentTicketForm'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import { TicketService } from '@/lib/services/ticket'

export default function ParentTicketEditPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const getTicketById = useParentTicketStore((s) => s.getTicketById)
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const ticket = id ? getTicketById(id) : undefined
  const meta = ticket as ParentTicketWithMeta | undefined
  const hasFullRequest = Boolean(meta?.request)
  const hasFullStore = Boolean(meta?.store) || !(meta?.store_id != null || (meta?.store_code != null && meta?.store_code !== ''))
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  // Fetch full ticket when not in store or when missing request or store details (e.g. from list API)
  useEffect(() => {
    if (!id) return
    if (ticket != null && hasFullRequest && hasFullStore) return
    let cancelled = false
    setFetchError(null)
    setFetching(true)
    TicketService.getParentTicket(id)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          mergeTicket(res.data)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError('Failed to load ticket')
      })
      .finally(() => {
        if (!cancelled) setFetching(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, ticket, hasFullRequest, hasFullStore, mergeTicket])

  const handleSuccess = () => {
    navigate('/tickets')
  }

  const handleCancel = () => {
    navigate('/tickets')
  }

  if (!id) {
    return null
  }

  if (fetching) {
    return (
      <Box p={4} display="flex" alignItems="center" justifyContent="center" minH="200px">
        <Spinner size="lg" />
        <Text ml={3}>Loading ticket...</Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">{fetchError}</Text>
            <Button variant="link" colorScheme="blue" mt={2} onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">Parent ticket not found.</Text>
            <Button variant="link" colorScheme="blue" mt={2} onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  return (
    <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
      <Box minW={0} display="flex" flexDirection="column" overflow="hidden" flex="1">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Edit Ticket — {ticket.ticketNumber ?? id}</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Update parent ticket details and assignment
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0}>
            <ParentTicketForm
              parentTicketId={id}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </CardBody>
        </Card>
      </Box>
    </Box>
  )
}
